import { DateTime } from 'luxon';
import {
	ScheduledReport,
	ScheduledReportsDrawerType,
	SchedulePayload,
} from 'src/types/scheduledReportTypes';

// Convert local time string (e.g., "19:00") to UTC time string (e.g., "13:30")
export function localTimeToUTC(time: string, zoneId: string): string {
	const [hour, minute] = time.split(':').map(Number);
	return DateTime.fromObject({ hour, minute }, { zone: zoneId })
		.toUTC()
		.toFormat('HH:mm');
}

// Convert UTC time string (e.g., "13:30") to local time string (e.g., "19:00")
export function utcTimeToLocal(time: string, zoneId: string): string {
	const [hour, minute] = time.split(':').map(Number);
	return DateTime.fromObject({ hour, minute }, { zone: 'utc' })
		.setZone(zoneId)
		.toFormat('HH:mm');
}

// Optionally round to nearest hour
export function roundToNearestHour(time: string): string {
	const [hour, minute] = time.split(':').map(Number);
	return minute >= 30
		? `${(hour + 1).toString().padStart(2, '0')}:00`
		: `${hour.toString().padStart(2, '0')}:00`;
}

export const validateEmails = (
	emailString: string,
): { isValid: boolean; error: string } => {
	if (!emailString.trim()) {
		return { isValid: false, error: 'At least one email is required' };
	}

	// Check for semicolon separator (common mistake)
	if (/;/.test(emailString)) {
		return {
			isValid: false,
			error: 'Please separate email addresses with commas',
		};
	}

	// Split by comma and trim
	const emails = emailString
		.split(',')
		.map((email) => email.trim())
		.filter((email) => email);

	// Check if any segment has multiple @ symbols (indicating missing comma separator)
	// or contains whitespace (e.g., "a@b.com c@d.com")
	const hasInvalidWhitespaceSeparator = emails.some((email) => {
		const atCount = (email.match(/@/g) || []).length;
		if (atCount > 1) {
			return true; // Multiple @ in one segment = missing comma
		}
		// Check for space or newline within what should be a single email
		if (/\s/.test(email) && /@/.test(email)) {
			return true;
		}
		return false;
	});

	if (hasInvalidWhitespaceSeparator) {
		return {
			isValid: false,
			error: 'Please separate email addresses with commas',
		};
	}

	if (emails.length > 30) {
		return { isValid: false, error: 'Maximum 30 emails allowed' };
	}

	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	const invalidEmails = emails.filter((email) => !emailRegex.test(email));

	if (invalidEmails.length > 0) {
		return {
			isValid: false,
			error: 'Please enter valid email addresses',
		};
	}

	const duplicates = emails.filter(
		(email, idx) => emails.indexOf(email) !== idx,
	);
	if (duplicates.length > 0) {
		return {
			isValid: false,
			error: 'Duplicate email addresses are not allowed',
		};
	}

	return { isValid: true, error: '' };
};

export const formatScheduleMessage = (
	status: 'success' | 'error',
	messageType: 'create' | 'update',
): string => {
	if (!status || !messageType) return '';

	if (status === 'success') {
		if (messageType === 'create') {
			return 'New schedule successfully created.';
		} else if (messageType === 'update') {
			return 'Your changes have been saved.';
		}
	} else if (status === 'error') {
		if (messageType === 'create') {
			return 'Error creating new schedule. Please try again after some time.';
		} else if (messageType === 'update') {
			return 'Error updating the schedule. Please try again after some time.';
		}
	}

	return '';
};

export const getOrdinalSuffix = (num: number): string => {
	const j = num % 10;
	const k = num % 100;
	if (j === 1 && k !== 11) {
		return num + 'st';
	}
	if (j === 2 && k !== 12) {
		return num + 'nd';
	}
	if (j === 3 && k !== 13) {
		return num + 'rd';
	}
	return num + 'th';
};

export const getTruncatedReportName = (
	name: string,
	maxLength: number,
): string => {
	if (name.length <= maxLength) return name;
	return name.substring(0, maxLength) + '...';
};

export const getSchedulePayload = (
	scheduledReport: ScheduledReportsDrawerType | ScheduledReport,
	localTime: string,
): SchedulePayload => {
	return {
		enabled: scheduledReport.enabled,
		format: scheduledReport.format,
		frequency: scheduledReport.frequency,
		frequencyDayOfMonth: scheduledReport.frequencyDayOfMonth,
		frequencyDayOfWeek: scheduledReport.frequencyDayOfWeek,
		frequencyMonthly: scheduledReport.frequencyMonthly,
		frequencyTime: localTimeToUTC(localTime, scheduledReport.zoneId),
		relativeDateRange: scheduledReport.relativeDateRange,
		reportId: scheduledReport.reportId,
		zoneId: scheduledReport.zoneId,
		recipients: scheduledReport.recipients,
	};
};

export const formatDateTime = (
	utcString: string | null,
	type: 'nextRun' | 'lastRun',
): string => {
	if (!utcString)
		return type === 'nextRun' ? 'N / A' : 'No email has been sent yet';
	return DateTime.fromISO(utcString).toFormat("MMM dd, yyyy 'at' hh:mm a");
};

// Map for frequencyMonthly
const monthlyMap: Record<string, string> = {
	FIRST: 'first',
	SECOND: 'second',
	THIRD: 'third',
	FOURTH: 'fourth',
	LAST: 'last',
	SPECIFIC_DATE: '',
};

// Map for days of week (0 = Sunday, 1 = Monday, ...)
const weekDayMap: Record<number, string> = {
	0: 'Sunday',
	1: 'Monday',
	2: 'Tuesday',
	3: 'Wednesday',
	4: 'Thursday',
	5: 'Friday',
	6: 'Saturday',
};

export function generateScheduleSummary(report: ScheduledReport): string {
	// Format time to local with timezone
	const localTime = DateTime.fromFormat(report.frequencyTime, 'HH:mm', {
		zone: report.zoneId,
	}).toFormat('h:mm a ZZZZ');

	// Relative date range
	const range = report.relativeDateRange
		.replace(/_/g, ' ')
		.toLowerCase()
		.replace(/^./, (c) => c.toUpperCase());

	let summary = `${range} ${report.frequency.toLowerCase()} report scheduled`;

	if (report.frequency === 'MONTHLY') {
		if (report.frequencyMonthly === 'SPECIFIC_DATE') {
			// Specific date (e.g., 9th)
			if (report.frequencyDayOfMonth === 0) {
				summary += ` on the last day of the month at ${localTime}`;
			} else {
				summary += ` on the ${getOrdinalSuffix(
					report.frequencyDayOfMonth,
				)} at ${localTime}`;
			}
		} else {
			// e.g., every fourth Monday
			const week = monthlyMap[report.frequencyMonthly];
			const day = weekDayMap[report.frequencyDayOfWeek];
			summary += ` every ${week} ${day} at ${localTime}`;
		}
	} else if (report.frequency === 'WEEKLY') {
		const day = weekDayMap[report.frequencyDayOfWeek];
		summary += ` on ${day} at ${localTime}`;
	} else if (report.frequency === 'DAILY') {
		summary += ` at ${localTime}`;
	}

	return summary;
}

export const convertScheduledReportToLocalTime = (
	report: ScheduledReport,
): ScheduledReport => {
	return {
		...report,
		frequencyTime: utcTimeToLocal(report.frequencyTime, report.zoneId),
	};
};
